"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Application = void 0;
const logger_1 = require("@leavy/logger");
const platform_1 = require("./platform");
const hooks_1 = require("../hooks");
const container_1 = require("../module/container");
const utils_1 = require("@leavy/utils");
const resolver_1 = require("../dependency/resolver");
const lifecycle_1 = require("./lifecycle");
const hooks_2 = require("../plugin/hooks");
const hooks_3 = require("../module/hooks");
class Application {
    constructor(logger, config) {
        this.logger = logger;
        this.config = config;
        this.isStarted = false;
        this.moduleContainers = new Map();
        this.pluginResolver = new resolver_1.DependencyResolver(this.logger);
        this.moduleResolver = new resolver_1.DependencyResolver(this.logger);
        this.hooks = new hooks_1.HookPipeline(this.logger);
    }
    get rootContainer() {
        return this.config.rootContainer;
    }
    get applicationContext() {
        return {
            logger: this.logger,
            hooks: this.hooks,
            rootContainer: this.rootContainer,
            platform: platform_1.getPlatform(),
            exitApplication: async () => {
                await this.stop();
            },
        };
    }
    registerGlobalDependencies() {
        this.rootContainer.bind(logger_1.Logger).toConstantValue(this.logger);
    }
    resolvePlugins() {
        for (const [ctor, config] of this.config.plugins) {
            this.pluginResolver.register(ctor, config);
        }
        this.resolvedPlugins = this.pluginResolver.build();
        // Add to hooks pipeline
        for (const { instance } of this.resolvedPlugins) {
            this.hooks.addObjectHandler(instance);
        }
    }
    resolveModules() {
        for (const [ctor, config] of this.config.modules) {
            this.moduleResolver.register(ctor, config);
        }
        this.resolvedModules = this.moduleResolver.build();
        // Add to hooks pipeline
        for (const { instance } of this.resolvedModules) {
            this.hooks.addObjectHandler(instance);
        }
    }
    async installModules() {
        const context = this.applicationContext;
        await this.hooks.execute(new hooks_3.BeforeModulesInstallationHook(this, context, this.resolvedModules));
        // Modules initialization
        for (const { instance, config } of this.resolvedModules) {
            // Create module container
            const moduleContainer = container_1.createModuleContainer(context.rootContainer);
            this.moduleContainers.set(instance, moduleContainer);
            await instance.initialize(config);
        }
        // Services registration
        for (const { instance, config } of this.resolvedModules) {
            const moduleContainer = this.moduleContainers.get(instance);
            utils_1.assert(moduleContainer, "Module container should exist for this module.");
            await instance.registerServices(moduleContainer, config);
        }
        // Services configuration
        for (const { instance, config } of this.resolvedModules) {
            const moduleContainer = this.moduleContainers.get(instance);
            utils_1.assert(moduleContainer, "Module container should exist for this module.");
            await instance.configureServices(moduleContainer, config);
        }
        await this.hooks.execute(new hooks_3.AfterModulesInstallationHook(this, context, this.resolvedModules));
    }
    async installPlugins() {
        const context = this.applicationContext;
        await this.hooks.execute(new hooks_2.BeforePluginsInstallationHook(this, context, this.resolvedPlugins));
        for (const { instance, config } of this.resolvedPlugins) {
            await instance.install(context, config);
        }
        await this.hooks.execute(new hooks_2.AfterPluginsInstallationHook(this, context, this.resolvedPlugins));
    }
    async startPlugins() {
        const context = this.applicationContext;
        for (const { instance } of this.resolvedPlugins) {
            await instance.start(context);
        }
    }
    async start() {
        if (this.isStarted) {
            this.logger.warn("Application is already started.");
            return this;
        }
        this.logger.info("Register global dependencies");
        this.registerGlobalDependencies();
        this.logger.info("Resolving plugins");
        this.resolvePlugins();
        this.logger.info("Resolving modules");
        this.resolveModules();
        this.logger.info("Installing plugins");
        await this.installPlugins();
        this.logger.info("Installing modules");
        await this.installModules();
        await this.hooks.execute(new lifecycle_1.ApplicationReadyHook(this, this.applicationContext));
        this.logger.info("Starting plugins");
        await this.startPlugins();
        this.logger.info("Plugins started");
        await this.hooks.execute(new lifecycle_1.ApplicationStartedHook(this, this.applicationContext));
        this.isStarted = true;
        return this;
    }
    async stop() {
        if (!this.isStarted) {
            this.logger.warn("Application is already stopped.");
            return this;
        }
        await this.hooks.execute(new lifecycle_1.BeforeApplicationExitHook(this));
        this.isStarted = false;
        return this;
    }
}
exports.Application = Application;
//# sourceMappingURL=application.js.map